import { createFileRoute, Link } from '@tanstack/react-router'
import { DashboardLayout } from '@/components/layout'
import { atsNav } from '@/lib/atsNav'
import { currentTenant, currentUser, STAGES } from '@/lib/mocks'
import { useCandidates } from '@/lib/candidateStore'
import { Button } from '@/components/ui/button'
import { MapPin } from 'lucide-react'
import { PageHeader, MatchScore, Avatar, SourceTag } from '@/lib/atsUi'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/pipeline')({
  component: PipelinePage,
})

function PipelinePage() {
  const allCandidates = useCandidates()
  return (
    <DashboardLayout
      title="Pipeline"
      navSections={atsNav}
      tenant={currentTenant}
      user={currentUser}
    >
      <PageHeader
        title="Pipeline"
        subtitle="Every candidate, by stage — just glance and click."
        action={
          <Button size="sm" variant="outline" asChild>
            <Link to="/jobs">All jobs</Link>
          </Button>
        }
      />

      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max">
          {STAGES.map((stage) => {
            const candidates = allCandidates.filter((c) => c.stage === stage.key)
            return (
              <div key={stage.key} className="flex flex-col min-w-[256px] max-w-[256px]">
                {/* Column header */}
                <div className="flex items-center justify-between mb-2 px-1">
                  <h2 className="text-sm font-medium text-muted-foreground">
                    {stage.label}
                  </h2>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {candidates.length}
                  </span>
                </div>

                {/* Column body */}
                <div className="bg-muted/30 rounded-lg p-2 space-y-2 flex-1 min-h-[120px]">
                  {candidates.length === 0 ? (
                    <div className="flex items-center justify-center h-16">
                      <span className="text-xs text-muted-foreground">Empty</span>
                    </div>
                  ) : (
                    candidates.map((c) => (
                      <Link
                        key={c.id}
                        to="/candidates/$candidateId"
                        params={{ candidateId: c.id }}
                        className={cn(
                          'block bg-card border border-border rounded-md p-3 cursor-pointer',
                          'hover:border-primary/40 transition-colors duration-150',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                        )}
                      >
                        {/* Top row: avatar + name + match score */}
                        <div className="flex items-center gap-2">
                          <Avatar name={c.name} />
                          <span className="font-medium text-sm text-foreground flex-1 truncate">
                            {c.name}
                          </span>
                          <MatchScore score={c.matchScore} />
                        </div>

                        {/* Sub: title · company */}
                        <p className="text-xs text-muted-foreground mt-1 truncate">
                          {c.title}
                          {c.currentCompany ? (
                            <span> · {c.currentCompany}</span>
                          ) : null}
                        </p>

                        {/* Bottom row: location + source */}
                        <div className="flex items-center justify-between mt-2 gap-1">
                          <div className="flex items-center gap-1 text-muted-foreground min-w-0">
                            <MapPin className="size-3 shrink-0" />
                            <span className="text-xs truncate">{c.location}</span>
                          </div>
                          <SourceTag source={c.source} />
                        </div>
                      </Link>
                    ))
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </DashboardLayout>
  )
}
